/**
 * Event Types for Universal Architecture
 * Centralized type definitions for all events in the system
 */

/**
 * Base event interface that all events must implement
 */
export interface BaseEvent {
    /** Unique event identifier */
    id: string;
    /** Event type identifier */
    type: string;
    /** Event timestamp */
    timestamp: number;
    /** Source component that triggered the event */
    source: string;
    /** Chart ID for chart-specific events */
    chartId?: string;
    /** Optional event data */
    data?: Record<string, any>;
    /** Optional metadata */
    metadata?: Record<string, any>;
}

/**
 * Modifier lifecycle events
 */
export enum ModifierLifecycleEventType {
    CREATED = 'modifier:created',
    INITIALIZED = 'modifier:initialized',
    RENDERED = 'modifier:rendered',
    UPDATED = 'modifier:updated',
    DESTROYED = 'modifier:destroyed'
}

export interface ModifierLifecycleEvent extends BaseEvent {
    type: ModifierLifecycleEventType;
    modifierId: string;
    modifierType: string;
    data?: {
        renderTime?: number;
        elementCount?: number;
        error?: string;
    };
}

/**
 * Data change events
 */
export enum DataChangeEventType {
    DATA_UPDATED = 'data:updated',
    DATA_DELETED = 'data:deleted',
    DATA_CREATED = 'data:created',
    DATA_BATCH_UPDATED = 'data:batch-updated'
}

export interface DataChangeEvent extends BaseEvent {
    type: DataChangeEventType;
    path: string;
    oldValue?: any;
    newValue?: any;
    changeType: 'create' | 'update' | 'delete' | 'batch';
}

/**
 * User interaction events
 */
export enum UserInteractionEventType {
    BUTTON_CLICK = 'ui:button-click',
    TOGGLE_CHANGE = 'ui:toggle-change',
    DROPDOWN_CHANGE = 'ui:dropdown-change',
    SLIDER_CHANGE = 'ui:slider-change',
    CUSTOM_INTERACTION = 'ui:custom-interaction'
}

export interface UserInteractionEvent extends BaseEvent {
    type: UserInteractionEventType;
    elementId: string;
    modifierId: string;
    interactionData: {
        value?: any;
        previousValue?: any;
        coordinates?: { x: number; y: number };
        [key: string]: any;
    };
}

/**
 * Chart navigation events
 */
export enum ChartNavigationEventType {
    CHART_ACTIVATED = 'chart:activated',
    CHART_DEACTIVATED = 'chart:deactivated',
    CHART_LOADED = 'chart:loaded',
    CHART_UNLOADED = 'chart:unloaded'
}

export interface ChartNavigationEvent extends BaseEvent {
    type: ChartNavigationEventType;
    chartId: string;
    previousChartId?: string;
    activationData?: {
        activationType: 'tab-click' | 'url-change' | 'programmatic';
        timestamp: number;
    };
}

/**
 * Chart sharing events
 */
export enum ChartShareEventType {
    SHARED_CHART_LOADED = 'chart:shared:loaded',
    SHARED_CHART_CREATED = 'chart:shared:created',
    SHARED_CHART_ERROR = 'chart:shared:error'
}

export interface ChartShareEvent extends BaseEvent {
    type: ChartShareEventType;
    shortCode?: string;
    chartData?: any;
    error?: string;
}

/**
 * System events
 */
export enum SystemEventType {
    ZOOM_CHANGED = 'system:zoom-changed',
    LAYOUT_RESIZED = 'system:layout-resized',
    VISIBILITY_CHANGED = 'system:visibility-changed',
    PERFORMANCE_WARNING = 'system:performance-warning',
    ERROR_OCCURRED = 'system:error'
}

export interface SystemEvent extends BaseEvent {
    type: SystemEventType;
    systemData: {
        zoom?: number;
        dimensions?: { width: number; height: number };
        isVisible?: boolean;
        performanceMetric?: string;
        error?: Error;
        [key: string]: any;
    };
}

/**
 * Debug events (only in development)
 */
export enum DebugEventType {
    DEBUG_LOG = 'debug:log',
    DEBUG_WARN = 'debug:warn',
    DEBUG_ERROR = 'debug:error',
    DEBUG_TRACE = 'debug:trace'
}

export interface DebugEvent extends BaseEvent {
    type: DebugEventType;
    level: 'log' | 'warn' | 'error' | 'trace';
    message: string;
    details?: any;
}

/**
 * Chakra toggle events for Panel B proxy system
 * Enables communication between original toggles and Panel B
 */
export const CHAKRA_TOGGLE_EVENT_TYPE = 'chakra.toggle' as const;

export interface ChakraToggleEventData {
    /** Unique toggle identifier (e.g., 'd7-toggle', 'nakshatra-toggle') */
    toggleId: string;
    /** Human-readable toggle label (e.g., 'D7 Saptamsa', 'Nakshatra Chakra') */
    toggleName: string;
    /** Toggle enabled state */
    enabled: boolean;
    /** Chart ID this toggle belongs to */
    chartId: string;
}

export interface ChakraToggleEvent extends BaseEvent {
    type: typeof CHAKRA_TOGGLE_EVENT_TYPE;
    /** Source toggle ID that emitted the event */
    from: string;
    /** Target toggle ID ('*' for broadcast, specific ID for direct message) */
    to: string;
    /** Event payload */
    data: ChakraToggleEventData;
}

/**
 * Union type of all possible events
 */
export type UniversalEvent =
    | ModifierLifecycleEvent
    | DataChangeEvent
    | UserInteractionEvent
    | ChartNavigationEvent
    | ChartShareEvent
    | SystemEvent
    | DebugEvent
    | ChakraToggleEvent;

/**
 * Event listener function type
 */
export type EventListener<T extends BaseEvent = UniversalEvent> = (event: T) => void | Promise<void>;

/**
 * Event filter function type
 */
export type EventFilter<T extends BaseEvent = UniversalEvent> = (event: T) => boolean;

/**
 * Event subscription options
 */
export interface EventSubscriptionOptions {
    /** Only listen to events from specific source */
    sourceFilter?: string | string[];
    /** Custom event filter function */
    filter?: EventFilter;
    /** Execute listener only once */
    once?: boolean;
    /** Throttle events (ms between executions) */
    throttle?: number;
    /** Debounce events (ms delay after last event) */
    debounce?: number;
    /** Priority for event handling (higher = earlier) */
    priority?: number;
}

/**
 * Event subscription handle for unsubscribing
 */
export interface EventSubscription {
    id: string;
    eventType: string;
    listener: EventListener;
    options: EventSubscriptionOptions;
    unsubscribe: () => void;
}

/**
 * Event emission options
 */
export interface EventEmissionOptions {
    /** Whether to emit synchronously (default: false) */
    sync?: boolean;
    /** Skip throttling/debouncing for this emission */
    immediate?: boolean;
    /** Custom metadata to attach */
    metadata?: Record<string, any>;
}

/**
 * Helper type for event type extraction
 */
export type EventTypeFromEnum<T> = T extends { [K in keyof T]: infer U } ? U : never;

/**
 * Helper to create strongly typed event creators
 */
export type EventCreator<T extends BaseEvent> = (
    source: string,
    data: Omit<T, keyof BaseEvent>
) => T;

/**
 * Create a generic event
 */
export function createEvent(
    type: string,
    source: string,
    data?: any,
    metadata?: Record<string, any>
): BaseEvent {
    return {
        id: `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type,
        source,
        timestamp: Date.now(),
        metadata: { ...data, ...metadata }
    };
}
